import { useCallback, useEffect, useState } from "react";

import Card from "../components/ui/Card";
import { useAuth } from "../context/AuthContext";

import {
  BatchForm,
  LastQuote,
  OrderDetail,
  OrderSummary,
  QuoteHistoryItem,
  RateForm,
  RateResult,
} from "../features/lynxpack/types";

import {
  DEFAULT_CP_DESTINATIONS,
  packagesToTextareaValue,
  cpListToTextareaValue,
  STANDARD_PACKAGE_PRESETS,
} from "../features/lynxpack/presets";

import { ManualQuotePanel } from "../features/lynxpack/components/ManualQuotePanel";
import { BatchQuotePanel } from "../features/lynxpack/components/BatchQuotePanel";
import { ResultsCards } from "../features/lynxpack/components/ResultsCards";
import { QuoteHistoryTable } from "../features/lynxpack/components/QuoteHistoryTable";
import { ServiceSelection } from "../features/lynxpack/components/ServiceSelection";
import { RecipientForm } from "../features/lynxpack/components/RecipientForm";
import { OrderPanel } from "../features/lynxpack/components/OrderPanel";
import { OrdersTable } from "../features/lynxpack/components/OrdersTable";
import { PaymentAlerts } from "../features/lynxpack/components/PaymentAlerts";
import { fetchOrders, fetchQuoteHistory } from "../features/lynxpack/api";

const initialForm: RateForm = {
  postalCodeOrigin: "",
  postalCodeDestination: "",
  deliveredType: "D",
  weight: "",
  height: "",
  width: "",
  length: "",
  declaredValue: "",
};

const initialBatchForm: BatchForm = {
  postalCodeOrigin: "",
  postalCodeDestinations: cpListToTextareaValue(DEFAULT_CP_DESTINATIONS),
  deliveredType: "ALL",
  packages: packagesToTextareaValue(STANDARD_PACKAGE_PRESETS),
};

export default function LynxPack() {
  // Cobros a revisar: lo ven quienes operan LynxPack (db_access) y
  // Liquidaciones (finance_access), igual que en el servidor.
  const { capabilities, rol } = useAuth();
  const verCobrosARevisar = capabilities?.db_access === true || capabilities?.finance_access === true;
  // Reintentar un despacho fallido y cancelar y reembolsar: un ADMIN que
  // opera LynxPack (db_access), igual que en el servidor.
  const operaLynxPack = rol === "ADMIN" && capabilities?.db_access === true;

  const [form, setForm] = useState<RateForm>(initialForm);
  const [batchForm, setBatchForm] = useState<BatchForm>(initialBatchForm);
  const [results, setResults] = useState<RateResult[]>([]);
  const [history, setHistory] = useState<QuoteHistoryItem[]>([]);
  const [historyError, setHistoryError] = useState("");
  const [loading, setLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [error, setError] = useState("");
  const [batchProgress, setBatchProgress] = useState("");

  // Los dos modos de cotización comparten una sola tarjeta con un switch; el
  // estado de cada formulario vive acá, así cambiar de modo no pierde lo cargado.
  const [modoCotizacion, setModoCotizacion] = useState<"manual" | "masiva">("manual");

  // El flujo de la orden (fase 6): de una cotización guardada se elige un
  // servicio, se carga el destinatario, y la orden creada se sigue en su
  // panel: cobrar, despachar, etiqueta.
  const [lastQuote, setLastQuote] = useState<LastQuote | null>(null);
  const [rateElegida, setRateElegida] = useState<RateResult | null>(null);
  const [ordenAbierta, setOrdenAbierta] = useState<string | null>(null);
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [ordersError, setOrdersError] = useState("");

  // El historial vive en la base desde la fase 2: sobrevive a un refresh y es
  // lo que respalda por qué se cobró determinado monto.
  const cargarHistorial = useCallback(async () => {
    try {
      const { quotes } = await fetchQuoteHistory({ limit: 50 });
      setHistory(quotes);
      setHistoryError("");
    } catch (err) {
      setHistoryError(
        err instanceof Error ? err.message : "No se pudo cargar el historial."
      );
    }
  }, []);

  const cargarOrdenes = useCallback(async () => {
    try {
      const { orders: filas } = await fetchOrders({ limit: 25 });
      setOrders(filas);
      setOrdersError("");
    } catch (err) {
      setOrdersError(
        err instanceof Error ? err.message : "No se pudieron listar las órdenes."
      );
    }
  }, []);

  useEffect(() => {
    cargarHistorial();
    cargarOrdenes();
  }, [cargarHistorial, cargarOrdenes]);

  const abrirOrdenCreada = (orden: OrderDetail) => {
    setRateElegida(null);
    setOrdenAbierta(orden.id);
    cargarOrdenes();
  };

  return (
    <div
      style={{
        minHeight: "100%",
        background: "var(--background)",
        color: "var(--text-primary)",
      }}
    >
      {/* Sin padding ni maxWidth propios: el <main> de AdminLayout ya aplica
          los 24 px. Duplicarlos desalineaba las tarjetas respecto del header. */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 24 }}>
        <Card>
          <p
            style={{
              margin: 0,
              color: "var(--primary)",
              fontSize: 13,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            Envíos
          </p>

          <h1
            style={{
              margin: "6px 0 0",
              color: "var(--text-primary)",
              fontSize: 32,
              fontWeight: 800,
            }}
          >
            LynxPack
          </h1>

          <p
            style={{
              margin: "8px 0 0",
              color: "var(--text-secondary)",
              maxWidth: 820,
              lineHeight: 1.5,
            }}
          >
            Cotizá con Correo Argentino y Andreani a la vez, generá la orden, cobrala con Mercado
            Pago (link para compartir) y despachala. Cada orden guarda su
            precio congelado, sus pagos y su historial completo.
          </p>
        </Card>

        {error && (
          <Card>
            <div
              style={{
                color: "var(--danger)",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          </Card>
        )}

        <section
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(360px, 420px) minmax(0, 1fr)",
            gap: 24,
            alignItems: "start",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr)",
              gap: 24,
              minWidth: 0,
            }}
          >
            <Card>
              <h2 style={{ margin: "0 0 14px", color: "var(--text-primary)", fontSize: 20 }}>
                Cotizar
              </h2>

              <div
                style={{
                  display: "flex",
                  gap: 4,
                  padding: 4,
                  background: "var(--surface-alt)",
                  border: "1px solid var(--border)",
                  borderRadius: 14,
                  marginBottom: 18,
                }}
              >
                {(["manual", "masiva"] as const).map((modo) => (
                  <button
                    key={modo}
                    type="button"
                    onClick={() => setModoCotizacion(modo)}
                    style={{
                      flex: 1,
                      padding: "9px 10px",
                      borderRadius: 10,
                      border: "none",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 700,
                      transition: "all var(--transition-base, 0.15s ease)",
                      background:
                        modoCotizacion === modo ? "var(--primary)" : "transparent",
                      color:
                        modoCotizacion === modo ? "#ffffff" : "var(--text-secondary)",
                    }}
                  >
                    {modo === "manual" ? "Uno a uno" : "Masiva"}
                  </button>
                ))}
              </div>

              {modoCotizacion === "manual" ? (
                <ManualQuotePanel
                  form={form}
                  setForm={setForm}
                  loading={loading}
                  setLoading={setLoading}
                  batchLoading={batchLoading}
                  setError={setError}
                  setResults={setResults}
                  setHistory={setHistory}
                  setLastQuote={setLastQuote}
                />
              ) : (
                <BatchQuotePanel
                  batchForm={batchForm}
                  setBatchForm={setBatchForm}
                  loading={loading}
                  batchLoading={batchLoading}
                  setBatchLoading={setBatchLoading}
                  setError={setError}
                  batchProgress={batchProgress}
                  setBatchProgress={setBatchProgress}
                  setHistory={setHistory}
                />
              )}
            </Card>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr)",
              gap: 24,
              minWidth: 0,
            }}
          >
            {ordenAbierta ? (
              <OrderPanel
                orderId={ordenAbierta}
                onClose={() => setOrdenAbierta(null)}
                onChanged={cargarOrdenes}
                puedeOperar={operaLynxPack}
              />
            ) : rateElegida && lastQuote ? (
              <RecipientForm
                quote={lastQuote}
                rate={rateElegida}
                onCreated={abrirOrdenCreada}
                onCancel={() => setRateElegida(null)}
              />
            ) : (
              <Card>
                <h2
                  style={{
                    margin: "0 0 18px",
                    color: "var(--text-primary)",
                    fontSize: 20,
                  }}
                >
                  Resultado uno a uno
                </h2>

                {lastQuote ? (
                  <ServiceSelection quote={lastQuote} onPick={setRateElegida} />
                ) : (
                  <ResultsCards results={results} />
                )}
              </Card>
            )}

            {verCobrosARevisar && <PaymentAlerts onOpen={(id) => setOrdenAbierta(id)} />}

            <OrdersTable
              orders={orders}
              error={ordersError}
              onOpen={(id) => setOrdenAbierta(id)}
            />

            <QuoteHistoryTable history={history} error={historyError} />
          </div>
        </section>
      </div>
    </div>
  );
}
